import React, { useState, useEffect } from "react";
import { AcmeCoOperationErrorIcon } from "../../assets/icons";
import axios from "axios";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { reqToUpdateSettings } from "../../redux/slices.js/settings/settingsSlice";

const Security = ({ settings = [] }) => {
  const dispatch = useDispatch();
  // Convert settings array → object { name: value }
  const initialState = settings.reduce((acc, s) => {
    acc[s.name] = s.value;
    return acc;
  }, {});

  const [formValues, setFormValues] = useState({
    enforce_2fa: false,
    enable_ip_whitelist: false,
    min_password_length: "",
    password_expiry_days: "",
    audit_log_retention_days: "",
    require_uppercase: false,
    require_lowercase: false,
    require_number: false,
    ...initialState, // override defaults with actual settings
  });

  // Generic toggle/field update
  const handleChange = (field, value) => {
    setFormValues((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSaveChanges = () => {
    const payload = Object.entries(formValues).map(([name, value]) => ({
      name,
      value: value instanceof Date ? value.toISOString() : value,
    }));

    dispatch(
      reqToUpdateSettings({
        data: payload,
        onSuccess: (response) => {
          // any additional success logic here
        },
        onFailure: (error) => {
          console.error("Failed to update settings:", error);
          // any additional failure logic here
        },
      })
    );
  };

  const primaryToggles = [
    { id: "enforce_2fa", label: "Enforce Two-Factor Authentication" },
    { id: "enable_ip_whitelist", label: "Enable IP Whitelist" },
  ];

  const securityToggles = [
    { id: "require_uppercase", label: "Require Uppercase" },
    { id: "require_lowercase", label: "Require Lowercase" },
    { id: "require_number", label: "Require Number" },
  ];

  return (
    <>
      <div className="space-y-4">
        <div className="bg-[#EDF7FF] rounded-xl sm:p-6 p-3">
          <div className="flex flex-col xl:flex-row md:items-start gap-4 md:justify-between">
            <div className="flex items-center gap-3">
              <span className="min-[40px]">{AcmeCoOperationErrorIcon}</span>
              <div>
                <span className="text-[16px] font-semibold mb-2 block">
                  Primary Administrator account
                </span>
                <p className="text-[16px] text-gray-500 text-sm mb-0">
                  This will be the main account for the tenant
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid gap-4 md:gap-6 md:grid-cols-2 border-b-[1px] border-[#EBEBEB] pb-7">
          {primaryToggles.map((toggle) => (
            <div
              key={toggle.id}
              className="flex items-center justify-start gap-4"
            >
              <button
                type="button"
                role="switch"
                aria-checked={formValues[toggle.id]}
                onClick={() => handleChange(toggle.id, !formValues[toggle.id])}
                className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                  formValues[toggle.id] ? "bg-[#4285F4]" : "bg-[#E5E7EB]"
                }`}
              >
                <span
                  aria-hidden="true"
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    formValues[toggle.id]
                      ? "translate-x-[10px]"
                      : "translate-x-0"
                  }`}
                />
              </button>
              <span className="text-black text-span">{toggle.label}</span>
            </div>
          ))}
        </div>

        <div className="form-group space-y-4">
          <label className="!text-black">Password Policy</label>
          <div className="grid gap-4 md:gap-6 xl:gap-2 md:grid-cols-2 xl:grid-cols-3">
            <div className="form-group">
              <label>
                Minimum Password Length <span>*</span>
              </label>
              <input
                type="number"
                placeholder="10"
                value={formValues.min_password_length}
                onChange={(e) =>
                  handleChange("min_password_length", e.target.value)
                }
              />
            </div>
            <div className="form-group">
              <label>
                Password Expiry (days) <span>*</span>
              </label>
              <input
                type="number"
                placeholder="10"
                value={formValues.password_expiry_days}
                onChange={(e) =>
                  handleChange("password_expiry_days", e.target.value)
                }
              />
            </div>
            <div className="form-group">
              <label>
                Audit Log Retention (days) <span>*</span>
              </label>
              <input
                type="number"
                placeholder="365 Days"
                value={formValues.audit_log_retention_days}
                onChange={(e) =>
                  handleChange("audit_log_retention_days", e.target.value)
                }
              />
            </div>
          </div>

          <div className="grid gap-4 md:gap-6 md:grid-cols-2 lg:grid-cols-3">
            {securityToggles.map((toggle) => (
              <div
                key={toggle.id}
                className="flex items-center justify-start gap-4"
              >
                <button
                  type="button"
                  role="switch"
                  aria-checked={formValues[toggle.id]}
                  onClick={() =>
                    handleChange(toggle.id, !formValues[toggle.id])
                  }
                  className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                    formValues[toggle.id] ? "bg-[#4285F4]" : "bg-[#E5E7EB]"
                  }`}
                >
                  <span
                    aria-hidden="true"
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      formValues[toggle.id]
                        ? "translate-x-[10px]"
                        : "translate-x-0"
                    }`}
                  />
                </button>
                <span className="text-black text-span">{toggle.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className={`mt-auto flex gap-3 pt-6 justify-end flex-wrap`}>
        <button type="button" className="next-btn" onClick={handleSaveChanges}>
          Save Security changes
        </button>
      </div>
    </>
  );
};

export default Security;
